
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import { EntryCard } from "@/components/Entries/EntryCard";
import { Button } from "@/components/ui/button";

import { SharedNotesHeader } from "@/components/SharedNotes/Header";
import { SharedNotesSearchBar } from "@/components/SharedNotes/SearchBar";
import { SharedNotesEmptyState } from "@/components/SharedNotes/EmptyState";
import { SharedNotesLoadingState } from "@/components/SharedNotes/LoadingState";
import { 
  fetchPublicEntries, 
  searchPublicEntries, 
  stripHtmlTags,
  type Entry 
} from "@/services/sharedNotes.service";

const SharedNotes = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (user) {
      handleFetchPublicEntries();
    }
  }, [user]);

  const handleFetchPublicEntries = async () => {
    setLoading(true);
    console.log("Fetching public entries for user:", user?.id);
    
    try {
      const publicEntries = await fetchPublicEntries(user?.id);
      console.log("Entries fetched:", publicEntries.length);
      setEntries(publicEntries);
    } catch (error: any) {
      console.error("Error fetching public entries:", error);
      toast.error("Failed to load public entries");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (!query.trim()) {
      handleFetchPublicEntries();
      return;
    }

    setSearching(true);
    try {
      const searchResults = await searchPublicEntries(query, user?.id);
      setEntries(searchResults);
    } catch (error: any) {
      console.error("Error searching public entries:", error);
      toast.error("Failed to search public entries");
    } finally {
      setSearching(false);
    }
  };

  const copyShareLink = (entry: Entry) => {
    const shareUrl = `${window.location.origin}/shared/${entry.share_id}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success("Share link copied to clipboard");
  };

  const addToPersonalDashboard = async (entry: Entry) => {
    if (!user) {
      toast.error("You must be logged in to add entries");
      return;
    }

    try {
      // Create a new entry for the current user based on the shared entry
      const { error } = await supabase
        .from("entries")
        .insert({
          title: `${entry.title} (Shared)`,
          content: entry.content,
          entry_type: entry.entry_type,
          creator_id: user.id,
          is_public: false,
          is_favorite: false,
          password: entry.password,
        })
        .select();

      if (error) throw error;

      // Track the save for analytics
      try {
        await supabase.rpc('track_entry_save', {
          p_entry_id: entry.id,
          p_user_id: user.id
        });
      } catch (saveError) {
        console.error("Error tracking save:", saveError);
        // Don't fail the whole operation if save tracking fails
      }

      toast.success("Added to your dashboard");
    } catch (error) {
      console.error("Error adding to dashboard:", error);
      toast.error("Failed to add entry to dashboard");
    }
  };

  const toggleFavorite = async (entry: Entry) => {
    return;
  };

  const togglePinned = async (entry: Entry) => {
    return;
  };

  console.log("Render SharedNotes with entries:", entries.length);

  return (
    <div className="space-y-6">
      <SharedNotesHeader onRefresh={handleFetchPublicEntries} />
      
      <SharedNotesSearchBar 
        onSearch={handleSearch} 
        loading={searching} 
      />

      {loading ? (
        <SharedNotesLoadingState />
      ) : entries.length === 0 ? (
        <SharedNotesEmptyState 
          searchQuery={searchQuery} 
          onRefresh={handleFetchPublicEntries} 
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {entries.map((entry) => (
            <div key={entry.id} className="relative group">
              <EntryCard
                entry={entry}
                currentUserId={user?.id}
                stripHtmlTags={stripHtmlTags}
                toggleFavorite={toggleFavorite}
                togglePinned={togglePinned}
                copyShareLink={copyShareLink}
                addToPersonalDashboard={addToPersonalDashboard}
              />
              <Button
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                size="sm"
                onClick={() => addToPersonalDashboard(entry)}
              >
                Add to My Notes
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SharedNotes;
